import { IContextWrapper } from '@vivli/shared/infrastructure/interface';
import { NotificationContext } from '@vivli/shared/features/notification/infrastructure/context';

import { useEffect, useRef, useState } from 'react';
import { first } from 'rxjs/operators';
import { INotification } from '@vivli/shared/features/notification/infrastructure/interface';
import { useFormState } from 'react-hook-form';
import { NotificationTypeEnum } from '@vivli/shared/features/notification/infrastructure/enum';
import {
    useActiveUserContext,
    useAppConfigContext,
    useModalApiContext,
    useNotificationApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { useFormDirtyTracking } from '@vivli/shared/infrastructure/hook';
import { notificationFormDefaultValues, useNotificationForm } from '@vivli/shared/features/notification/infrastructure/hook';

export const NotificationContextWrapper = ({ children }: IContextWrapper) => {
    const toastService = useToastApiContext();
    const modalService = useModalApiContext();
    const { user: activeUser } = useActiveUserContext();
    const notificationService = useNotificationApiContext();
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [topicId, setTopicId] = useState<string>();
    const config = useAppConfigContext();
    const activeEnv = config.queryUri.split('-')[1];
    const [showForm, setShowForm] = useState<boolean>(false);
    const [lastSentMessageState, setlastSentMessageState] = useState<string>('N/A');
    const [firstAndLastDates, setFirstAndLastDates] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>();

    const formApi = useNotificationForm();
    const { isValid: isFormValid } = useFormState({ control: formApi.control });
    const isFormDirty = useFormDirtyTracking(formApi);
    const lastLoadedNotificationId = useRef<string>();

    const inactiveConfirmMsg = 'Are you sure you want to leave the notification inactive?';

    //load the fetched notification into the form once per notification. Keyed on the id so a
    //re-render can't reset a form the user is part-way through editing.
    useEffect(() => {
        const notification = notifications[0];
        if (!notification || lastLoadedNotificationId.current === notification.id) {
            return;
        }

        lastLoadedNotificationId.current = notification.id;
        formApi.reset(notification);
    }, [notifications]);

    const handleSaveSuccess = () => {
        toastService.success('Successfully saved your notification');
        setIsSaving(false);
        formApi.reset(formApi.getValues());
        getNotifications(topicId);
    };
    const handleFailure = (err: string) => {
        modalService.error(`An error occurred submitting notifications. Please try again or contact Vivli support. Message: ${err}`);
        setIsSaving(false);
    };

    const handleDates = (datesObj) => {
        if (datesObj.lastSendFailed) {
            setlastSentMessageState('Failed');
        }

        const dates = datesObj.lastAndNextSendDates;
        const previousDate = dates[0]?.toString().substring(0, 10);
        const nextDate = dates[1]?.toString().substring(0, 10);
        setFirstAndLastDates([previousDate, nextDate]);
        setShowForm(true);
    };
    const handleForm = (notificationArr) => {
        setNotifications(notificationArr);

        // if notifications exists
        if (notificationArr?.length > 0) {
            setShowForm(false);
            const notificationId = parseInt(notificationArr[0].id);
            notificationService
                .getLastAndNextDate(notificationId)
                .pipe(first())
                .subscribe((datesObj) => handleDates(datesObj), handleFailure);
        }

        //  if no notifications exist we get an empty array
        if (notificationArr?.length < 1) {
            setShowForm(true);
        }
    };

    const getNotifications = (topicId: string) => {
        setTopicId(topicId);
        notificationService
            .getNotificationsForTopic(topicId)
            .pipe(first())
            .subscribe((notificationArr) => handleForm(notificationArr), handleFailure);
    };

    const createNewNotification = (updatedNotificationForm, topicType) => {
        const newNotification = {
            ...updatedNotificationForm,
            topicId: topicId,
            createdBy: activeUser.userId,
            updatedBy: activeUser.userId,
            environment: activeEnv,
            topicType: topicType,
            notificationType: NotificationTypeEnum.ChatMessage
        };
        setIsSaving(true);

        if (newNotification.active == false) {
            modalService.confirm(inactiveConfirmMsg, {
                title: 'Please Confirm',
                confirmText: 'Ok',
                cancelText: 'Cancel',
                onConfirm: () => {
                    notificationService.createNotification(newNotification).pipe(first()).subscribe(handleSaveSuccess, handleFailure);
                },
                onCancel: () => {
                    setIsSaving(false);
                }
            });
        } else {
            notificationService.createNotification(newNotification).pipe(first()).subscribe(handleSaveSuccess, handleFailure);
        }
    };
    const handleUpdate = (updatedNotificationForm) => {
        const updatedNotification = { ...updatedNotificationForm, updatedBy: activeUser.userId };
        setIsSaving(true);

        if (updatedNotification.active == false) {
            modalService.confirm(inactiveConfirmMsg, {
                title: 'Please Confirm',
                confirmText: 'Ok',
                cancelText: 'Cancel',
                onConfirm: () => {
                    notificationService.updateNotification(updatedNotification).pipe(first()).subscribe(handleSaveSuccess, handleFailure);
                },
                onCancel: () => {
                    setIsSaving(false);
                }
            });
        } else {
            notificationService.updateNotification(updatedNotification).pipe(first()).subscribe(handleSaveSuccess, handleFailure);
        }
    };

    const handleSubmit = (topicType) => {
        const updatedNotificationForm: INotification = formApi.getValues();

        if (notifications.length <= 0) {
            createNewNotification(updatedNotificationForm, topicType);
        } else {
            handleUpdate(updatedNotificationForm);
        }
    };
    const resetNotificationForm = () => {
        setShowForm(false);

        modalService.confirm('Are you sure you want to create a new notification?', {
            title: 'Please Confirm',
            confirmText: 'Ok',
            cancelText: 'Cancel',
            onConfirm: () => {
                setNotifications([]);
                setFirstAndLastDates([]);
                lastLoadedNotificationId.current = undefined;
                //back to a blank notification, not to whichever one was last loaded - otherwise
                //the create would post the loaded notification's id and fail on insert
                formApi.reset(notificationFormDefaultValues);
                setShowForm(true);
            },
            onCancel: () => {
                setShowForm(true);
            }
        });
    };

    const provider = {
        getNotifications,
        resetNotificationForm,
        notifications,
        handleSubmit,
        showForm,
        firstAndLastDates,
        lastSentMessageState,
        isSaving,
        formApi,
        isFormValid,
        isFormDirty
    };

    return <NotificationContext.Provider value={provider}>{children}</NotificationContext.Provider>;
};
